import React from 'react';
import { Fade, Grid, Card, Box, Typography, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MeetingRoom as MeetingRoomIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Chart from 'react-apexcharts';

const MeetingsTab = ({ data = {} }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const completed  = data.completed  || 0;
  const scheduled  = data.scheduled  || data.pending || 0; // backward-compat
  const started    = data.started    || 0;
  const cancelled  = data.cancelled  || 0;
  const total      = data.total      || (completed + scheduled + started + cancelled);
  const completionRate  = data.completionRate  || 0;
  const averageDuration = data.averageDuration || 0;
  const recentMeetings  = data.recentMeetings  || 0;

  // Stacked bar chart
  const stackedChartData = {
    options: {
      chart: {
        type: 'bar',
        stacked: true,
        stackType: '100%',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
      },
      xaxis: {
        categories: ['Total Sessions'],
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      yaxis: {
        max: 100,
        labels: {
          formatter: (val) => `${val}%`,
        },
      },
      colors: [theme.palette.success.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.error.main],
      grid: {
        borderColor: theme.palette.divider,
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => {
          const num = Math.round((val / 100) * total);
          return num > 0 ? num : '';
        },
        style: { fontSize: '12px', fontWeight: 'bold', colors: ['#fff'] },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '50%',
          borderRadius: 4,
        },
      },
      legend: {
        position: 'top',
        fontSize: '14px',
        fontWeight: 500,
      },
      tooltip: {
        theme: theme.palette.mode,
        y: {
          formatter: (val) => {
            const num = Math.round((val / 100) * total);
            return `${num} sessions (${val.toFixed(1)}%)`;
          },
        },
      },
    },
    series: [
      { name: 'Completed',   data: [total > 0 ? (completed  / total) * 100 : 0] },
      { name: 'In Progress', data: [total > 0 ? (started    / total) * 100 : 0] },
      { name: 'Scheduled',   data: [total > 0 ? (scheduled  / total) * 100 : 0] },
      { name: 'Cancelled',   data: [total > 0 ? (cancelled  / total) * 100 : 0] },
    ],
  };

  // Line chart with markers for meeting trends
  const lineChartData = {
    options: {
      chart: {
        type: 'line',
        animations: { enabled: true, easing: 'easeinout', speed: 800 },
        toolbar: { show: false },
      },
      stroke: { width: 3, curve: 'smooth' },
      markers: { size: 6, hover: { size: 8 } },
      xaxis: {
        categories: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        labels: { style: { fontSize: '12px', fontWeight: 500 } },
      },
      yaxis: {
        title: { text: 'Sessions', style: { fontSize: '14px', fontWeight: 600 } },
      },
      colors: ['#2e7d32', '#ed6c02'],
      legend: { position: 'top', fontSize: '14px', fontWeight: 500 },
      dataLabels: { enabled: true },
    },
    series: [
      {
        name: 'Completed',
        data: [
          Math.round(completed * 0.7),
          Math.round(completed * 0.85),
          Math.round(completed * 0.95),
          completed,
        ],
      },
      {
        name: 'Scheduled',
        data: [
          Math.round((completed + scheduled) * 0.7),
          Math.round((completed + scheduled) * 0.85),
          Math.round((completed + scheduled) * 0.95),
          completed + scheduled,
        ],
      },
    ],
  };

  return (
    <Fade in timeout={500}>
      <Grid container spacing={3}>
        {/* Large Stat Cards - Top Row */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0px 4px 12px 0px rgba(102, 126, 234, 0.3)',
              borderRadius: 2,
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <MeetingRoomIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
                {total}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                Total Sessions
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 48, mb: 1, color: 'success.main' }} />
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.titles' }}>
                {completed}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Completed
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <PendingIcon sx={{ fontSize: 48, mb: 1, color: 'warning.main' }} />
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.titles' }}>
                {scheduled}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Scheduled
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 3,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.customShadows.card,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 48, mb: 1, color: 'primary.main' }} />
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5, color: 'text.titles' }}>
                {averageDuration}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Avg Duration (min)
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Stacked Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUpIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                Session Status Breakdown
              </Typography>
            </Box>
            <Chart options={stackedChartData.options} series={stackedChartData.series} type="bar" height={300} />
          </Card>
        </Grid>

        {/* Line Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: 3,
              boxShadow: '0px 2px 6px 0px rgba(13, 10, 44, 0.08)',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MeetingRoomIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.titles' }}>
                Session Trends (4 Weeks)
              </Typography>
            </Box>
            <Chart options={lineChartData.options} series={lineChartData.series} type="line" height={300} />
          </Card>
        </Grid>

        {/* Bottom Stats row – 4 equal cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (t) => t.palette.background.paper,
              boxShadow: (t) => t.customShadows.card,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {completionRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Completion Rate
                </Typography>
              </Box>
              <Chip
                label={completionRate >= 80 ? 'Excellent' : completionRate >= 60 ? 'Good' : 'Needs Improvement'}
                color={completionRate >= 80 ? 'success' : completionRate >= 60 ? 'warning' : 'error'}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (t) => t.palette.background.paper,
              boxShadow: (t) => t.customShadows.card,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {cancelled}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Cancelled Sessions
                </Typography>
              </Box>
              <CancelIcon sx={{ fontSize: 32, color: 'error.main' }} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              p: 2.5,
              background: (t) => t.palette.background.paper,
              boxShadow: (t) => t.customShadows.card,
              borderRadius: 2,
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: 'text.titles' }}>
                  {recentMeetings}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Recent (30 days)
                </Typography>
              </Box>
              <Chip label="Recent" color="info" sx={{ fontWeight: 600 }} />
            </Box>
          </Card>
        </Grid>

        {/* 4th card – navigate to Meeting Details page */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            onClick={() => navigate('/dashboard/meeting-details')}
            sx={{
              p: 2.5,
              height: '100%',
              borderRadius: 2,
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
              boxShadow: '0 4px 16px rgba(168,70,155,0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(168,70,155,0.45)',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
                  View Details
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  All session records
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 28, color: '#fff' }} />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default MeetingsTab;

